import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight, Heart, Loader, MapPin, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import authAxios from "../../authAxios";
import instance from "../../axios";
import Toast from "../../components/common/Toast";
import { useGlobalState } from "../../GlobalStates";
import { Footer, StickyHeader } from "../../intro/components/landing-new";
import IdeaDetailModal from "./components/IdeaDetailModal";
import IdeaFormModal from "./components/IdeaFormModal";
import { TRAVEL_CATEGORIES, useTravelCategories } from "./constants";
import { useTranslation } from "react-i18next";

const Ideas = () => {
  const { t } = useTranslation();
  const [ideas, setIdeas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);
  const [votedIdeas, setVotedIdeas] = useState(new Set());
  const [newIdea, setNewIdea] = useState({
    destination: "",
    description: "",
    category: "",
    images: [],
    imagePreviews: [],
  });

  const [user] = useGlobalState("user");
  const categories = useTravelCategories();

  useEffect(() => {
    fetchIdeas();
  }, []);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewIdea((prev) => ({
          ...prev,
          images: [...prev.images, file],
          imagePreviews: [...prev.imagePreviews, reader.result],
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (index) => {
    setNewIdea((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
      imagePreviews: prev.imagePreviews.filter((_, i) => i !== index),
    }));
  };

  const fetchIdeas = async () => {
    try {
      const response = await instance.get("/v1/ideas", {
        params: {
          userId: user.id,
        },
      });
      setIdeas(response.data);
      if (response.data.length > 0 && selectedIdea) {
        setSelectedIdea(response.data.find((idea) => idea.id === selectedIdea.id));
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching ideas:", error);
      setIsLoading(false);
    }
  };

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleVote = async (id) => {
    if (votedIdeas.has(id)) {
      return;
    }

    try {
      await authAxios.post(`/v1/ideas/${id}/vote`, {
        increment: true,
        userId: user.id,
      });
      setVotedIdeas((prev) => new Set([...prev, id]));
      await fetchIdeas();
      showToast(t("ideas.vote_success"), "success");
    } catch (error) {
      showToast(error.response.data.error || t("ideas.vote_error"), "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);

    try {
      const formData = new FormData();
      formData.append("destination", newIdea.destination);
      formData.append("description", newIdea.description);
      if (newIdea.category) {
        formData.append("category", newIdea.category);
      }
      newIdea.images.forEach((image, index) => {
        formData.append(`images[${index}]`, image);
      });

      if (user.id) {
        formData.append("userId", user.id);
      }

      await authAxios.post("/v1/ideas", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setShowForm(false);
      setNewIdea({
        destination: "",
        description: "",
        category: "",
        images: [],
        imagePreviews: [],
      });

      showToast(t("ideas.submit_success"), "celebration");

      await fetchIdeas();
    } catch (error) {
      console.error("Error submitting idea:", error);
      showToast(t("ideas.submit_error"), "error");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const filteredIdeas = ideas.filter(
    (idea) =>
      (idea.destination.toLowerCase().includes(searchQuery.toLowerCase()) ||
        idea.description.toLowerCase().includes(searchQuery.toLowerCase())) &&
      (!selectedCategory || idea.category === selectedCategory)
  );

  return (
    <div className="tw-min-h-screen tw-bg-gradient-to-br tw-from-sky-50 tw-via-white tw-to-blue-50 tw-px-4 tw-pb-10 tw-pt-24 sm:tw-px-6 lg:tw-px-8">
      <StickyHeader withoutCta />
      <div className="tw-mx-auto tw-min-h-[100vh] tw-max-w-4xl">
        <div className="tw-text-center">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="tw-mb-8 tw-bg-gradient-to-r tw-from-[deepskyblue] tw-to-blue-600 tw-bg-clip-text tw-text-4xl tw-font-bold tw-text-transparent"
          >
            {t("ideas.title")}
          </motion.h1>

          <div className="tw-mb-8 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 sm:tw-flex-row">
            <button
              onClick={() => setShowForm(!showForm)}
              className="tw-rounded-full tw-bg-gradient-to-r tw-from-[deepskyblue] tw-to-blue-500 tw-px-8 tw-py-4 tw-font-semibold tw-text-white tw-shadow-lg tw-transition-all hover:tw-shadow-xl"
            >
              {t("ideas.share_button")}
            </button>

            <div className="tw-relative tw-w-full sm:tw-w-96">
              <Search className="tw-absolute tw-left-3 tw-top-1/2 tw-h-5 tw-w-5 -tw-translate-y-1/2 tw-text-gray-400" />
              <input
                type="text"
                placeholder={t("ideas.search_placeholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="tw-w-full tw-rounded-full tw-border-0 tw-bg-white tw-py-4 tw-pl-10 tw-pr-4 tw-shadow tw-transition-all focus:tw-ring-2 focus:tw-ring-[deepskyblue]"
              />
            </div>
          </div>

          <div className="tw-mb-12 tw-flex tw-flex-wrap tw-justify-center tw-gap-2">
            <button
              onClick={() => setSelectedCategory("")}
              className={`tw-rounded-full tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-transition-all ${
                !selectedCategory
                  ? "tw-bg-[deepskyblue] tw-text-white"
                  : "tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50"
              }`}
            >
              {t("ideas.all_categories")}
            </button>
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                className={`tw-flex tw-items-center tw-space-x-2 tw-rounded-full tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-transition-all ${
                  selectedCategory === category.value
                    ? "tw-bg-[deepskyblue] tw-text-white"
                    : "tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50"
                }`}
              >
                <span>{category.icon}</span>
                <span>{category.label}</span>
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <IdeaFormModal
            onClose={() => setShowForm(false)}
            newIdea={newIdea}
            setNewIdea={setNewIdea}
            handleSubmit={handleSubmit}
            loadingSubmit={loadingSubmit}
            handleImageChange={handleImageChange}
            handleRemoveImage={handleRemoveImage}
            open={showForm}
          />

          <IdeaDetailModal
            selectedIdea={selectedIdea}
            onClose={() => setSelectedIdea(null)}
            handleVote={handleVote}
            open={!!selectedIdea}
          />

          {toast && (
            <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          )}
        </AnimatePresence>

        <div className="tw-space-y-4">
          {isLoading ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12">
              <motion.div
                animate={{
                  rotate: 360,
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Loader className="tw-h-8 tw-w-8 tw-text-[deepskyblue]" />
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="tw-mt-4 tw-text-gray-500"
              >
                {t("ideas.loading")}
              </motion.p>
            </div>
          ) : filteredIdeas.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="tw-py-12 tw-text-center"
            >
              <motion.div
                animate={{
                  y: [0, -10, 0],
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <MapPin className="tw-mx-auto tw-mb-4 tw-h-16 tw-w-16 tw-text-gray-300" />
              </motion.div>
              <motion.h3
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="tw-mb-2 tw-text-xl tw-font-semibold tw-text-gray-900"
              >
                {t("ideas.no_results_title")}
              </motion.h3>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="tw-text-gray-500"
              >
                {t("ideas.no_results_description")}
              </motion.p>
            </motion.div>
          ) : (
            <AnimatePresence mode="popLayout" initial={false}>
              {filteredIdeas
                .sort((a, b) => b.votes - a.votes)
                .map((idea, index) => (
                  <motion.div
                    layout="position"
                    key={idea.id}
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -20 }}
                    transition={{
                      opacity: { duration: 0.2 },
                      scale: { duration: 0.3 },
                      layout: { duration: 0.3 },
                      delay: Math.min(index * 0.05, 0.5),
                    }}
                    className="tw-group tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-bg-white tw-p-4 tw-shadow-sm tw-transition-shadow hover:tw-shadow-md"
                  >
                    <div className="tw-flex tw-grow tw-items-center tw-space-x-4">
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        className="tw-flex tw-cursor-pointer tw-items-center tw-space-x-2"
                        onClick={() => handleVote(idea.id)}
                      >
                        <Heart className="tw-h-5 tw-w-5 tw-text-[deepskyblue]" />
                        <motion.span
                          layout="position"
                          className="tw-font-medium"
                          transition={{ duration: 0.3 }}
                        >
                          {idea.votes}
                        </motion.span>
                      </motion.div>
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="tw-flex tw-items-center tw-space-x-2"
                      >
                        <MapPin className="tw-h-5 tw-w-5 tw-text-gray-400" />
                        <span className="tw-font-medium">{idea.destination}</span>
                      </motion.div>
                    </div>

                    <motion.button
                      whileHover={{ x: 8, scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      onClick={() => setSelectedIdea(idea)}
                      className="tw-rounded-full tw-p-2 tw-opacity-0 tw-transition-opacity hover:tw-bg-sky-50 group-hover:tw-opacity-100"
                    >
                      <ChevronRight className="tw-h-5 tw-w-5 tw-text-[deepskyblue]" />
                    </motion.button>
                  </motion.div>
                ))}
            </AnimatePresence>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Ideas;
